import { useState, useEffect } from 'react';

/**
 * Station House Officer contact details, sourced from the TN Police Citizen
 * Portal and keyed by "<district_lower>|<ps_code>" (PS codes repeat across
 * districts, so the district is part of the key). Built by
 * tn-police-scraper/build_nammamap_contacts.py.
 */
export interface PoliceStationContact {
  officer_name?: string;
  phone?: string;
  mobile?: string;
  email?: string;
}

interface ContactsFile {
  generated_on: string;
  source: string;
  by_key: Record<string, PoliceStationContact>;
}

let cached: ContactsFile | null = null;
let loadPromise: Promise<ContactsFile | null> | null = null;

const loadDataset = async (): Promise<ContactsFile | null> => {
  if (cached) return cached;
  if (loadPromise) return loadPromise;

  loadPromise = fetch('/data/police/police_contacts.json')
    .then(r => {
      if (!r.ok) throw new Error('Failed to fetch police contacts');
      return r.json();
    })
    .then((data: ContactsFile) => {
      cached = data;
      return data;
    })
    .catch(e => {
      // Enrichment is optional — never block the card if this dataset is missing.
      console.warn('Police contacts dataset unavailable:', e);
      loadPromise = null;
      return null;
    });

  return loadPromise;
};

const makeKey = (district?: string | null, code?: string | null): string | null => {
  if (!district || !code) return null;
  return `${district.trim().toLowerCase()}|${code.trim()}`;
};

/**
 * Look up the SHO contact for a station by its district + PS code.
 * Returns null while loading or when no contact is on record (graceful).
 */
export const usePoliceContact = (
  district?: string | null,
  psCode?: string | null,
): PoliceStationContact | null => {
  const [contact, setContact] = useState<PoliceStationContact | null>(null);

  useEffect(() => {
    const key = makeKey(district, psCode);
    if (!key) {
      setContact(null);
      return;
    }

    let mounted = true;
    loadDataset().then(data => {
      if (mounted) setContact(data?.by_key[key] ?? null);
    });

    return () => { mounted = false; };
  }, [district, psCode]);

  return contact;
};
